import React, {useState, useEffect, useMemo} from "react";

import { Link } from 'react-router-dom';
import { Button, Table, message, Modal, Space, Upload } from "antd";
import { UploadOutlined, BarChartOutlined, DatabaseOutlined, WarningTwoTone, ArrowRightOutlined } from '@ant-design/icons';
import moment from 'moment';

import { getData, getLocationOrigin } from '../../config/api-connect';

import SummaryLayout from "../../components/layout/SummaryLayout";
import MonthSelector from "../../components/ui/MonthSelector";
import { useMonthFilter } from "../../hooks/useMonthFilter";

function RazorpaySummary() {
  const { selectedMonth, setSelectedMonth } = useMonthFilter();
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [fileList, setFileList] = useState([])
  const [uploadLoading, setUploadLoading] = useState(false)
  const [summaryLoader, setSummaryLoader] = useState(false)
  const [summaryData, setSummaryData] = useState([])

  useEffect(() => {
    get_razorpay_summary()
  },[selectedMonth])

  const summaryTotals = useMemo(() => {
    if (!summaryData?.length) {
      return {
        razorpay_amount: 0,
        system_collection: 0,
        difference: 0,
      };
    }

    return summaryData.reduce(
      (acc, r) => {
        acc.razorpay_amount += r.razorpay_amount || 0;
        acc.system_collection += r.collection_amount || 0;
        acc.difference += r.difference || 0;
        return acc;
      },
      {
        razorpay_amount: 0,
        system_collection: 0,
        difference: 0,
      }
    );
  }, [summaryData]);
  
  const formatNumber = (value) =>
    Math.round(value).toLocaleString("en-IN");

  const statsConfig = [
    { 
      key: "razorpay_amount",
      title: "Razorpay Collection",
      icon: <BarChartOutlined style={{ fontSize: 35, color: "#1890ff" }} />,
    },
    {
      key: "system_collection",
      title: "System Collection",
      icon: <DatabaseOutlined style={{ fontSize: 35, color: "#722ed1" }} />,
    },
    {
      key: "difference",
      title: "Difference",
      icon: <WarningTwoTone twoToneColor="#faad14" style={{ fontSize: 35 }} />,
    },
  ];

  const handleOpen = () => {
    setIsUploadOpen(true)
    setFileList([])
    setUploadLoading(false)
  }
  
  const handleClose = () => {
    setIsUploadOpen(false)
    setFileList([])
    setUploadLoading(false)
  }

  const handleUpload = async () => {
    setUploadLoading(true)
    if (fileList.length === 0) {
      message.warning("Please select a file before uploading!");
      return;
    }

    const file = fileList[0].originFileObj;
    try {
      const data = await uploadFileToAPI(file);
      message.success(data.message || "File uploaded successfully!");
      handleClose()
    } catch (error) {
      message.error(error.message || "Something went wrong!");
    } finally {
      setUploadLoading(false);
      get_razorpay_summary()
    }
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList.slice(-1))
  };

  const uploadFileToAPI = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const url = `${getLocationOrigin()}/finance/upload_razorpay_transactions_from_excel/`;
    const options = {
      method: "POST",
      body: formData,
    };

    const response = await fetch(url, options);
    if (!response.ok) throw new Error("Upload failed");
    return response.json();
  };

  const get_razorpay_summary = () => {
    setSummaryLoader(true)

    var month = selectedMonth.format('MM')
    var year = selectedMonth.format('YYYY')

    getData(`/finance/get_razorpay_payments_summary/?month=${month}&year=${year}`)
    .then(response =>{
      setSummaryData(response.result)
      setSummaryLoader(false)
    })
    .catch(error => {
      setSummaryLoader(false)
      message.error(error.message || "Failed to fetch summary");
    });
  }

  const columns = [
    {
      title: "Month",
      dataIndex: "month",
    },
    {
      title: "Razorpay Collection",
      dataIndex: "razorpay_amount",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "System Collection",
      dataIndex: "collection_amount",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "Difference",
      dataIndex: "difference",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "",
      width: 20,
      align: "center",
      render: (_, record) => {
        const month = moment(record.month).format("MM");
        const year = moment(record.month).format("YYYY");

        return (
          <Link to={`/razorpay-collection/?month=${month}&year=${year}`}>
            <ArrowRightOutlined style={{ fontSize: "16px" }} />
          </Link>
        );
      }
    },
  ];

  const summaryCells = [
    { key: "razorpay_amount" },
    { key: "system_collection"},
    { key: "difference" },
  ];

  const renderSummary = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}>
        <b>Total</b>
      </Table.Summary.Cell>

      {summaryCells.map((item, i) => (
        <Table.Summary.Cell
          key={item.key}
          index={i + 2}
          align="right"
        >
          <b>{formatNumber(summaryTotals[item.key])}</b>
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );

  const renderRightSideActions = () => (
    <>
      <Button 
        type="primary" 
        icon={<UploadOutlined />} 
        style={{ borderRadius: 8 , color: 'white', backgroundColor: '#1565c0', borderColor:'#1565c0'}}
        onClick={handleOpen}
      >
        Upload Razorpay Transaction List
      </Button>
      <Modal
        title="Upload Razorpay Payment List"
        open={isUploadOpen}
        onCancel={handleClose}
        confirmLoading={uploadLoading}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            loading={uploadLoading}
            disabled={!fileList.length}
          >
            Upload
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Upload
            accept=".xlsx"
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false} 
          >
            <Button icon={<UploadOutlined />}>Select .xlsx File</Button>
          </Upload>
        </Space>
      </Modal>

      <MonthSelector
        value={selectedMonth}
        onChange={setSelectedMonth}
      />
    </>
  )

  return (
    <SummaryLayout
      title="Razorpay Summary"
      headerContent={renderRightSideActions()}
      statsConfig={statsConfig}
      totals={summaryTotals}
      statsLoading={summaryLoader}
      formatter={formatNumber}
      tableProps={{
        columns,
        data: summaryData,
        loading: summaryLoader,
        pagination: false,
      }}
      summary={summaryData.length ? renderSummary : undefined}
    />
  );
}

export default RazorpaySummary;